import { EI18nLanguageUI, useTranslation } from '@packages/utils';
import { Flex, RadioChangeEvent } from 'antd';
import { Radio, RadioGroup, Text } from '@idaho-aeyc/ui-kit';
import { Title } from 'components/Base/Typography';
import { useEffect, useMemo, useState } from 'react';
import ProgressStepper from '../../components/ProgressStepper';
import { stepOneKey } from '../../../RegistrationModal/RegistrationModal.data';
import { TRegistrationFormDataUI } from '../../../../../../data/types/events.types';
import { useGetEventsByIdQuery } from '../../../../../../redux/apiSlice/events.slice';
import useRegistrationContext, {
  LANGUAGE_OPTIONS,
  LANGUAGE_TRANSLATE,
} from '../../context/NewRegistrationModalContext';
import PartInfo from './PartInfo/PartInfo';
import LabeledInfo from '../../components/LabelInfo/LabelInfo';
import BackNextButtons from '../../components/BackNextButtons';
import { MobileDivider, StyledStepWrapper } from '../StepWrapper.style';
import { ELanguage } from '../../../../../../data/types/generic.types';
import { CapacityWarning } from '../../components/Warnings';

const EventInformation = ({ data }: { data?: TRegistrationFormDataUI }) => {
  const { t } = useTranslation({
    keyPrefix: stepOneKey,
    lng: 'translate',
  });
  const [showWarning, setShowWarning] = useState<boolean>(false);

  const {
    registrationValues,
    setRegistrationValues,
    englishGroupCapacities,
    spanishGroupCapacities,
    eventDetails,
    stepper,
    setStepper,
    resetValuesFromStepThreeOnwards,
  } = useRegistrationContext();

  const currentLang = registrationValues.userLanguage;
  const selectedLanguage = LANGUAGE_TRANSLATE[currentLang as EI18nLanguageUI];

  const translatedLanguage = useMemo(
    () =>
      LANGUAGE_TRANSLATE[registrationValues.userLanguage as EI18nLanguageUI],
    [registrationValues.userLanguage],
  );

  const displayPartsInfo = useMemo(
    () =>
      data?.eventType === 'READY_FOR_KINDERGARTEN' &&
      data?.eventCategory === 'SERIES_WITH_THREE_PARTS',
    [data],
  );

  const handleLanguageChange = (e: RadioChangeEvent) => {
    let lang: EI18nLanguageUI = EI18nLanguageUI.ENGLISH;

    if (e.target.value === ELanguage.SPANISH) {
      lang = EI18nLanguageUI.SPANISH;
    }

    if (lang !== registrationValues.userLanguage) {
      resetValuesFromStepThreeOnwards();
    }

    setRegistrationValues({
      ...registrationValues,
      userLanguage: lang,
    });
  };

  // Set warning if the only age group is full
  useEffect(() => {
    if (
      selectedLanguage === ELanguage.SPANISH &&
      spanishGroupCapacities &&
      Object.keys(spanishGroupCapacities).length === 1
    ) {
      Object.keys(spanishGroupCapacities).forEach(key => {
        if (spanishGroupCapacities[key].full) {
          setShowWarning(true);
        }
      });
    } else if (
      selectedLanguage === ELanguage.ENGLISH &&
      englishGroupCapacities &&
      Object.keys(englishGroupCapacities).length === 1
    ) {
      Object.keys(englishGroupCapacities).forEach(key => {
        if (englishGroupCapacities[key].full) {
          setShowWarning(true);
        }
      });
    }
  }, [englishGroupCapacities, spanishGroupCapacities]);

  const computedData = (data || eventDetails) as
    | TRegistrationFormDataUI
    | undefined;
  const eventIdForFallback = computedData?.eventId || '';
  const { data: eventDetailFallback } = useGetEventsByIdQuery(
    eventIdForFallback,
    { skip: !eventIdForFallback },
  );
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const eventLanguageText =
    ((computedData as any)?.eventLanguage as string) ||
    ((eventDetailFallback as any)?.eventLanguage as string) ||
    '';

  // Separate translation hook without keyPrefix for enums
  const { t: tEnums } = useTranslation({ lng: 'translate' });

  // Format event type using translations
  const formatEventType = (type: string) => {
    return tEnums(`enums.event-type.${type}`, { defaultValue: type });
  };

  // Get event title from fallback data
  const eventTitle = eventDetailFallback?.eventTitle || '';
  const eventType = computedData?.eventType || '';

  const displayTitle =
    eventType && eventTitle
      ? `${formatEventType(eventType)}  ·  ${eventTitle}`
      : formatEventType(eventType) || eventTitle;

  // @ts-ignore
  return (
    <>
      <ProgressStepper />
      <MobileDivider />
      <StyledStepWrapper>
        {/* Event Type - Event Title as Title */}
        {displayTitle && (
          <Title
            style={{ fontSize: '24px', marginBottom: 16 }}
            data-cy="registration-step1-title"
          >
            {displayTitle}
          </Title>
        )}

        <Flex vertical gap={10} style={{ marginBottom: 60 }}>
          {computedData?.locationAddress && (
            <LabeledInfo label={t('LOCATION_ADDRESS')} labelWidth={140}>
              <Text data-cy="registration-location-address">
                {computedData.locationAddress}
              </Text>
            </LabeledInfo>
          )}

          {computedData?.hostName && (
            <LabeledInfo label={t('HOSTED_BY')} labelWidth={140}>
              <Text data-cy="registration-host-name">
                {computedData.hostName}
              </Text>
            </LabeledInfo>
          )}

          <LabeledInfo label={t('LANGUAGE')} labelWidth={140}>
            <Text data-cy="registration-language-options">English</Text>
          </LabeledInfo>

          {/* Ages removed per requirement */}

          <LabeledInfo label={t('CHILD_CARE')} labelWidth={140}>
            <Text data-cy="registration-childcare-provided">
              {data?.childcareProvided ? (
                <>{t('PROVIDED')}</>
              ) : (
                <>{t('NOT_PROVIDED')}</>
              )}
            </Text>
          </LabeledInfo>

          <LabeledInfo
            label={t('MEAL')}
            labelWidth={140}
            style={{ marginBottom: 24 }}
          >
            <Text data-cy="registration-meal-provided">
              {data?.foodProvided ? (
                <>{t('PROVIDED')}</>
              ) : (
                <>{t('NOT_PROVIDED')}</>
              )}
            </Text>
          </LabeledInfo>

          {showWarning && (
            <CapacityWarning
              onClose={() => setShowWarning(false)}
              data-cy="registration-capacity-warning"
            />
          )}

          {displayPartsInfo && (
            <Text style={{ fontWeight: 600 }}>{t('DESCRIPTION')}</Text>
          )}

          <Flex vertical gap={16} style={{ marginBottom: 24 }}>
            {computedData?.parts?.map(part => (
              <PartInfo
                partType={part.partType}
                dateTimeString={part.dateTimeString}
                isPassed={part.isPassed}
                showPartLabel={(computedData?.parts?.length || 0) > 1}
                data-cy="registration-parts-info"
              />
            ))}
          </Flex>

          {computedData?.languageOptions?.length ? (
            <Flex vertical gap={8}>
              <Text>{t('CHOOSE_LANGUAGE')}</Text>
              <RadioGroup
                onChange={handleLanguageChange}
                value={translatedLanguage}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 8,
                  marginLeft: 10,
                }}
                data-cy="registration-language"
              >
                {computedData.languageOptions.map(option => (
                  <Radio value={option.value}>
                    {t(LANGUAGE_OPTIONS[option.value])}
                  </Radio>
                ))}
              </RadioGroup>
            </Flex>
          ) : null}
        </Flex>
      </StyledStepWrapper>
      <BackNextButtons
        isNextDisabled={!registrationValues.userLanguage}
        onNext={() => setStepper(stepper + 1)}
      />
    </>
  );
};
export default EventInformation;
